<template>
  <div class="mb-4">
    <div class="flex items-center gap-x-3 mb-4">
      <AtomIcon icon-type="outline" icon-name="ChartSquareBarIcon" color-variant="shy" />

      <AtomText
        data-cy="dashboard-key-indicators-title"
        :text="t('dashboard.keyIndicators.title')"
        font-size="text-lg"
        font-style="font-medium"
      />
    </div>

    <div class="grid md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2.5 mb-1">
      <MoleculeFrameCard
        v-for="keyIndicator in getKeyIndicators"
        :key="keyIndicator.key"
        :class="keyIndicator.classes"
        spacing="sm"
        data-cy="dashboard-key-indicator"
      >
        <template #header>
          <template v-if="isLoading">
            <MoleculeSkeleton variant="buttonExpanded" />
          </template>

          <template v-else>
            <div class="flex items-center justify-center gap-x-[2px]">
              <AtomText
                :text="String(keyIndicator.indicatorNumber || 0).replace('.', ',')"
                font-size="text-2xl"
                font-style="font-semibold"
                data-cy="dashboard-key-indicator-indicator-number"
              />

              <AtomText
                :text="
                  t(
                    `dashboard.keyIndicators.${keyIndicator.key}.title`,
                    Math.ceil(keyIndicator.indicatorNumber)
                  )
                "
                font-size="text-xs"
                data-cy="dashboard-key-indicator-indicator-number-title"
              />
            </div>

            <AtomText
              class="text-center uppercase"
              :text="
                t(
                  `dashboard.keyIndicators.${keyIndicator.key}.description`,
                  Math.ceil(keyIndicator.indicatorNumber)
                )
              "
              font-size="text-xs"
              font-style="font-medium"
              data-cy="dashboard-key-indicator-description"
            />
          </template>
        </template>

        <div class="flex justify-around items-center">
          <template v-if="isLoading">
            <MoleculeSkeleton variant="title" />
          </template>

          <template v-else>
            <AtomText
              :text="
                t(
                  `dashboard.keyIndicators.${keyIndicator.key}.subText`,
                  {
                    number: String(keyIndicator.subNumber).replace('.', ','),
                  },
                  Math.ceil(keyIndicator.subNumber)
                )
              "
              color-variant="description"
              font-size="text-xs"
              font-style="font-medium"
              data-cy="dashboard-key-indicator-sub-text"
            />

            <AtomIcon
              v-if="keyIndicator.hasExplanationModal"
              class="cursor-pointer"
              icon-name="InformationCircleIcon"
              icon-type="outline"
              @click="
                openExplanationModal(
                  keyIndicator.sectionKey as
                    'failures' | 'activationDeadline' | 'averageEquipmentsInstalled'
                )
              "
              data-cy="dashboard-key-indicator-infos-button"
            />
          </template>
        </div>
      </MoleculeFrameCard>
    </div>

    <div class="mt-1">
      <template v-if="isLoading">
        <MoleculeSkeleton class="w-[240px]" variant="singleText" />
      </template>
      <template v-else>
        <AtomText
          :text="timePassedSinceUpdate"
          font-size="text-sm"
          data-cy="dashboard-key-indicator-hours-passed"
        />
      </template>
    </div>
  </div>

  <ExplanationModalComponent
    :is-modal-visible="isExplanationModalOpened"
    :section-key="currentModalSectionKey"
    @update:isConditionsModalOpen="(value: boolean) => (isExplanationModalOpened = value)"
  />
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import type { IKeyIndicatorsType } from '@/types/dashboard.types';

import ExplanationModalComponent from './ExplanationModal.component.vue';
import {
  MYLIGHT_AVERAGE_INSTALLED_EQUIPMENTS_COUNT,
  MYLIGHT_FAILURES_RATE,
  MYLIGHT_MEDIAN_TIME_TO_ACTIVATION_IN_DAYS,
} from '@/constants';

type IKeyIndicatorsProps = {
  keyIndicators: IKeyIndicatorsType | null;
  timePassedSinceUpdate: string | null;
  isLoading: boolean;
};

const props = defineProps<IKeyIndicatorsProps>();

const { t } = useI18n();

const isExplanationModalOpened = ref(false);
const currentModalSectionKey = ref<
  'failures' | 'activationDeadline' | 'averageEquipmentsInstalled'
>('failures');

const lastIndicatorClasses = 'xl:col-span-3 2xl:col-span-1';

const getKeyIndicators = computed(() => [
  {
    indicatorNumber: props.keyIndicators?.installationsCountYearToDate || 0,
    subNumber: props.keyIndicators?.installationsCountOverall || 0,
    key: 'totalInstallations',
  },
  {
    indicatorNumber: props.keyIndicators?.failuresRate || 0,
    subNumber: MYLIGHT_FAILURES_RATE,
    key: 'failingInstallations',
    hasExplanationModal: true,
    sectionKey: 'failures',
  },
  {
    indicatorNumber: props.keyIndicators?.medianTimeToActivationInDays || 0,
    subNumber: MYLIGHT_MEDIAN_TIME_TO_ACTIVATION_IN_DAYS,
    key: 'activationRange',
    hasExplanationModal: true,
    sectionKey: 'activationDeadline',
  },
  {
    indicatorNumber: +(props.keyIndicators?.averageInstalledEquipmentsCount || 0).toFixed(2),
    subNumber: MYLIGHT_AVERAGE_INSTALLED_EQUIPMENTS_COUNT,
    key: 'equipmentsInstalled',
    hasExplanationModal: true,
    classes: lastIndicatorClasses,
    sectionKey: 'averageEquipmentsInstalled',
  },
]);

const openExplanationModal = (
  sectionKey: 'failures' | 'activationDeadline' | 'averageEquipmentsInstalled'
) => {
  currentModalSectionKey.value = sectionKey;
  isExplanationModalOpened.value = true;
};
</script>
